import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortingIcon from "../../globalStyles/icons/sorting-icon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { useTheme } from "@mui/styles";
import { shippingServiceMapService } from "../../utils/services/shippingServiceMapService";
import ShippingServiceMapDetailsModal from "../shipping-service-map-details";
import TableHeader from "../table-header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#EEEEEE",
    border: "1px solid #EAECF0",
    // color: theme.palette.common.black,
    padding: "4px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid #EAECF0",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid #EAECF0",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const ShippingServiceMapTableCell = ({
  shippingService,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
  handleClientAssignClick,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(shippingService);
        break;
      case "edit":
        handleEditClick(shippingService);
        break;
      case "delete":
        handleDeleteClick(shippingService);
        break;
      case "assign":
        handleClientAssignClick(shippingService);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: "10px" }}>
          <Button
            onClick={() => handleActionClick("edit")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => handleActionClick("view")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>

          <Button
            onClick={() => handleActionClick("delete")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Delete
              </Typography>
            </Box>
          </Button>
          {/* <Button onClick={() => handleActionClick('assign')} style={{ textTransform: 'none' }}>
            <Box display="flex" alignItems="center" justifyContent="flex-start" width="100%" gap={1}>
              <ClientAssignIcon />
              <Typography fontFamily={'poppins'} fontSize={'16px'} color={'var(--text-tertiary)'} fontWeight={500}>
                Client Assign
              </Typography>
            </Box>
          </Button> */}
        </Box>
      </Popover>
    </Box>
  );
};
const ShippingServiceMapList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedService,
  setSelectedShippingService,
  currentPage,
  itemsPerPage,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedShippingServiceData, setSelectedShippingServiceData] =
    useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const startIndex = currentPage * itemsPerPage;
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;

  const handleDeleteClick = (shippingService) => {
    setSelectedShippingService(shippingService);
    setOpenConfirmation(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await shippingServiceMapService.delete(
        selectedService._id,
      );
      onDeleteData && onDeleteData();
      setOpenConfirmation(false);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (shippingService) => {
    setSelectedShippingService(shippingService);
    setOpenViewModal(true);
  };

  const handleClientAssignClick = (x) => {
    setSelectedShippingService(x);
    setModalOpen(true);
  };
  useEffect(() => {
    setSelectedShippingServiceData(
      data?.map((shippingService) => ({
        ...shippingService,
        action: (
          <ShippingServiceMapTableCell
            shippingService={shippingService}
            handleViewClick={handleViewClick}
            handleEditClick={onEditData}
            handleDeleteClick={handleDeleteClick}
            handleClientAssignClick={handleClientAssignClick}
          />
        ),
      })),
    );
  }, [data]);

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", minHeight: "100%" }}
      />
    </div>
  );

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "service",
      title: "SERVICE",
      displayKey: "service",
      filterKey: "service",
      // handleOpenFilter: handleOpenFilter,
    },
    {
      key: "provider",
      title: "PROVIDER",
      displayKey: "provider",
      // handleOpenFilter: handleOpenFilter,
    },

    {
      key: "shipping_service",
      title: "SHIPPING SERVICE",
      displayKey: "shipping_service.service",
    },

    { key: "action", title: "", width: "3%", displayKey: "action" },
  ];
  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {selectedShippingServiceData?.map((shippingService, index) => (
        <Grid sx={{ paddingBottom: 1 }} item xs={12} sm={6} md={4} key={index}>
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid #D0D5DD",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {shippingService?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    SERVICE
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Tooltip title={shippingService?.service} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {shippingService?.service}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    PROVIDER
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={shippingService?.provider} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {shippingService?.provider}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    SHIPPING SERVICE
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip
                    title={shippingService?.shipping_service?.service}
                    arrow
                  >
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {shippingService?.shipping_service?.service}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        // paddingBottom: 100,
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      {isMobile() ? (
        renderGridView()
      ) : (
        <TableContainer
          //  className="custom-container"
          sx={{
            // maxHeight: "70vh",
            overflowY: "auto",
            paddingInline: 0.4,
            maxHeight: "100%",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              border: "1px solid var(--border-color)",
            }}
          >
            <TableHead>
              <StyledTableRow>
                {TableSchema.map((column) => (
                  <TableHeader
                    key={column.key}
                    column={column}
                    filterKey={column.filterKey}
                    handleOpenFilter={column.handleOpenFilter}
                    title={column.title}
                  />
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {selectedShippingServiceData.length === 0 ? (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <NoDataImage />
                  </TableCell>
                </StyledTableRow>
              ) : (
                selectedShippingServiceData?.map((x, rowIndex) => (
                  <StyledTableRow key={rowIndex}>
                    {TableSchema.map((column, colIndex) => (
                      <StyledTableCell
                        key={column.key || colIndex}
                        sx={{ width: column.width }}
                      >
                        {column.displayKey ? (
                          <Typography
                            fontSize="12px"
                            color="#667085"
                            fontWeight={400}
                            fontFamily={"Poppins"}
                          >
                            {getNestedValue(x, column.displayKey) || "--"}
                          </Typography>
                        ) : column.render ? (
                          column.render(x, rowIndex)
                        ) : (
                          "--"
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <div
          style={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : `calc(100vh - 250px)`,
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" ? renderGridView() : renderListView()}
        </div>
      )}

      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedService?.service} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={(e) => {
          confirmDelete(e);
        }}
      />

      <ShippingServiceMapDetailsModal
        shippingService={selectedService}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
    </div>
  );
};

export default ShippingServiceMapList;
